import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ERROR_PERIOD_OPTIONS,
  ErrorGroup,
  ErrorOccurrence,
  ErrorPeriod,
  ErrorStatus,
  SystemAdminErrorsService,
  errorWhere,
  parseReference,
} from '../../services/systemAdminErrorsService';
import { ErrorStatusBadge, formatWhen, orgLabel, timeAgo, useOrgNames } from './errorsCommon';

// Status filter. "Open" (new + investigating) is the default: it is the to-do
// list. Resolved groups that recur reopen as new by themselves.
const STATUS_FILTERS: { id: string; label: string; statuses: ErrorStatus[] }[] = [
  { id: 'open', label: 'Open', statuses: ['new', 'investigating'] },
  { id: 'new', label: 'New', statuses: ['new'] },
  { id: 'investigating', label: 'Investigating', statuses: ['investigating'] },
  { id: 'resolved', label: 'Resolved', statuses: ['resolved'] },
  { id: 'ignored', label: 'Ignored', statuses: ['ignored'] },
  { id: 'all', label: 'All', statuses: [] },
];

const PAGE_SIZE = 50;

/**
 * System Admin → Errors: every distinct server-side failure (a GROUP), with how
 * often it happened in the period and how many organisations it touched.
 * Opening one shows its occurrences and triage controls.
 *
 * Filters live in the URL so Back from a group returns to the same view.
 * The reference box takes what a user quotes ("Ref: …" from an error message)
 * and jumps straight to that failure.
 */
const Errors: React.FC = () => {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const orgNames = useOrgNames();

  const statusId = STATUS_FILTERS.some(f => f.id === params.get('status')) ? params.get('status')! : 'open';
  const period = (ERROR_PERIOD_OPTIONS.some(p => p.id === params.get('period')) ? params.get('period') : '7d') as ErrorPeriod;
  const orgParam = Number(params.get('org'));
  const orgId = Number.isInteger(orgParam) && orgParam > 0 ? orgParam : null;
  const pageParam = Number(params.get('page'));
  const page = Number.isInteger(pageParam) && pageParam > 0 ? pageParam : 1;

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key); else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next, { replace: true });
  };

  const [groups, setGroups] = useState<ErrorGroup[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const statuses = STATUS_FILTERS.find(f => f.id === statusId)!.statuses;
      const data = await SystemAdminErrorsService.fetchGroups({
        statuses, period, orgId, limit: PAGE_SIZE, offset: (page - 1) * PAGE_SIZE,
      });
      setGroups(data.groups);
      setTotal(data.total);
    } catch (err: any) {
      setError(err?.response?.data?.error || 'Failed to load errors.');
    } finally {
      setLoading(false);
    }
  }, [statusId, period, orgId, page]);

  useEffect(() => { load(); }, [load]);

  // Reference lookup.
  const [refText, setRefText] = useState('');
  const [refResult, setRefResult] = useState<{ ref: string; occurrences: ErrorOccurrence[] } | null>(null);
  const [refError, setRefError] = useState<string | null>(null);
  const [refLoading, setRefLoading] = useState(false);

  const findReference = async (e: React.FormEvent) => {
    e.preventDefault();
    const ref = parseReference(refText);
    setRefResult(null);
    setRefError(null);
    if (!ref) {
      setRefError('That doesn’t look like a reference. Paste the id after “Ref:”.');
      return;
    }
    setRefLoading(true);
    try {
      const occurrences = await SystemAdminErrorsService.findByReference(ref);
      if (occurrences.length === 1) {
        navigate(`/system-admin/errors/occurrences/${occurrences[0].id}`);
        return;
      }
      setRefResult({ ref, occurrences });
    } catch (err: any) {
      setRefError(err?.response?.data?.error || 'Search failed.');
    } finally {
      setRefLoading(false);
    }
  };

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const periodLabel = ERROR_PERIOD_OPTIONS.find(p => p.id === period)!.label;
  const orgOptions = Array.from(orgNames.entries()).sort((a, b) => a[1].localeCompare(b[1]));

  return (
    <div className="sys-page">
      <div className="sys-page__head">
        <p className="sys-page__eyebrow">Platform</p>
        <h1 className="sys-page__title">Errors</h1>
        <span className="sys-page__count">
          {loading && groups.length === 0 ? 'Loading…' : `${total} ${total === 1 ? 'error' : 'errors'} · last ${periodLabel}`}
        </span>
      </div>

      <div className="pill-toolbar" role="toolbar" aria-label="Error filters">
        <div className="sys-period" role="group" aria-label="Status">
          {STATUS_FILTERS.map(f => (
            <button key={f.id} type="button" className="sys-period__btn" aria-pressed={statusId === f.id}
                    onClick={() => setParam('status', f.id === 'open' ? null : f.id)}>
              {f.label}
            </button>
          ))}
        </div>
        <div className="sys-period ms-2" role="group" aria-label="Last seen within">
          {ERROR_PERIOD_OPTIONS.map(p => (
            <button key={p.id} type="button" className="sys-period__btn" aria-pressed={period === p.id}
                    onClick={() => setParam('period', p.id === '7d' ? null : p.id)}>
              {p.label}
            </button>
          ))}
        </div>
        <select className="form-select form-select-sm sys-errors__org ms-2" aria-label="Organisation"
                value={orgId ?? ''} onChange={e => setParam('org', e.target.value || null)}>
          <option value="">All organisations</option>
          {orgOptions.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
          {orgId && !orgNames.has(orgId) && <option value={orgId}>#{orgId}</option>}
        </select>
        <div className="pill-toolbar__spacer" />
        <button className="pill-toolbar__icon-btn" onClick={load} disabled={loading} title="Reload" aria-label="Reload errors">
          <i className="fas fa-sync-alt" style={loading ? { animation: 'fa-spin-smooth 0.7s linear infinite' } : undefined} />
        </button>
      </div>

      <div className="sys-page__body">
        <form className="ivory-panel sys-panel sys-panel--pad mb-3 sys-errors__ref" onSubmit={findReference}>
          <label htmlFor="error-ref" className="sys-panel__title mb-2 d-block">Find by reference</label>
          <div className="d-flex gap-2">
            <input id="error-ref" type="text" className="form-control form-control-sm" value={refText}
                   onChange={e => setRefText(e.target.value)}
                   placeholder="Paste the reference from the user’s error message, e.g. Ref: 3f2a9c1e-…" />
            <button type="submit" className="btn btn-sm btn-success" disabled={refLoading || !refText.trim()}>
              {refLoading ? 'Searching…' : 'Find'}
            </button>
          </div>
          {refError && <div className="text-danger small mt-2">{refError}</div>}
          {refResult && refResult.occurrences.length === 0 && (
            <div className="sys-panel__hint mt-2">
              Nothing recorded for <code>{refResult.ref}</code>. Errors are kept for 30 days, and only
              server-side failures are recorded — a refusal (e.g. a permissions or validation message) isn’t.
            </div>
          )}
          {refResult && refResult.occurrences.length > 1 && (
            <div className="mt-2">
              <div className="sys-panel__hint mb-1">
                {refResult.occurrences.length} errors for this reference (the request and any worker it started):
              </div>
              <table className="sys-table sys-table--rows sys-table--inline">
                <tbody>
                  {refResult.occurrences.map(o => (
                    <tr key={o.id} tabIndex={0}
                        onClick={() => navigate(`/system-admin/errors/occurrences/${o.id}`)}
                        onKeyDown={e => { if (e.key === 'Enter') navigate(`/system-admin/errors/occurrences/${o.id}`); }}>
                      <td>{formatWhen(o.occurredAt)}</td>
                      <td>{errorWhere(o)}</td>
                      <td>{orgLabel(orgNames, o.organisationId)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </form>

        {error ? (
          <div className="alert alert-danger">{error}</div>
        ) : loading && groups.length === 0 ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <div className="spinner-border text-success" role="status"><span className="visually-hidden">Loading…</span></div>
          </div>
        ) : groups.length === 0 ? (
          <div className="ivory-panel sys-panel sys-panel--pad text-center text-muted py-5">
            {statusId === 'open' ? `No open errors in the last ${periodLabel}.` : `No errors match these filters.`}
          </div>
        ) : (
          <div className={`ivory-panel sys-panel sys-panel--table ${loading ? 'sys-stale' : ''}`} aria-busy={loading}>
            <div className="sys-table-scroll">
              <table className="sys-table sys-table--rows">
                <thead>
                  <tr>
                    <th>Status</th>
                    <th>Event</th>
                    <th>Where</th>
                    <th>Error</th>
                    <th className="sys-table__num" title={`Occurrences in the last ${periodLabel}`}>Count</th>
                    <th className="sys-table__num" title={`Organisations affected in the last ${periodLabel}`}>Orgs</th>
                    <th>Last seen</th>
                  </tr>
                </thead>
                <tbody>
                  {groups.map(g => {
                    const open = () => navigate(`/system-admin/errors/${g.id}`);
                    return (
                      <tr key={g.id} tabIndex={0} onClick={open} onKeyDown={e => { if (e.key === 'Enter') open(); }}>
                        <td>
                          <ErrorStatusBadge status={g.status} />
                          {g.reopenedCount > 0 && (
                            <span className="sys-tag" title={`Came back ${g.reopenedCount}× after being resolved`}>
                              reopened{g.reopenedCount > 1 ? ` ×${g.reopenedCount}` : ''}
                            </span>
                          )}
                        </td>
                        <td className="sys-table__name"><code>{g.event}</code></td>
                        <td className="sys-table__muted">{errorWhere(g)}</td>
                        <td className="sys-errors__msg">
                          {(g.errCode || g.errName) && <span className="sys-tag me-1">{g.errCode || g.errName}</span>}
                          <span title={g.sampleMessage ?? undefined}>{g.sampleMessage || <span className="sys-table__muted">—</span>}</span>
                        </td>
                        <td className="sys-table__num">{(g.periodOccurrences ?? g.occurrenceCount).toLocaleString()}</td>
                        <td className="sys-table__num">{g.periodOrganisations ?? '—'}</td>
                        <td title={formatWhen(g.lastSeenAt)}>{timeAgo(g.lastSeenAt)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {pages > 1 && (
          <div className="d-flex justify-content-end align-items-center gap-2 mt-2">
            <button className="btn btn-sm btn-outline-secondary" disabled={page <= 1}
                    onClick={() => setParam('page', String(page - 1))}>Previous</button>
            <span className="small text-muted">Page {page} of {pages}</span>
            <button className="btn btn-sm btn-outline-secondary" disabled={page >= pages}
                    onClick={() => setParam('page', String(page + 1))}>Next</button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Errors;
